import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import axios from 'axios';
import classNames from 'classnames';
import { addLocale } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputSwitch } from 'primereact/inputswitch';
import { Dialog } from 'primereact/dialog';
import { Toolbar } from 'primereact/toolbar';
import { Calendar } from 'primereact/calendar';
import { Menu } from 'primereact/menu';
import { FileUpload } from 'primereact/fileupload';

import { environment } from "./util/baseUrl";

import { format } from "date-fns";


const emptyHistoria = {
    id: null,
    titulo: '',
    descripcion: '',
    redactor: '',
    urlImagen: null,
    fechaAcontecimiento: null,
    fechaPublicacion: null
};

const PJ_BLUE_DARK = '#003366';
const PJ_GRAY_BUTTON = '#6c757d';

addLocale('es', {
    firstDayOfWeek: 1,
    dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
    monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    today: 'Hoy',
    clear: 'Limpiar',
});


const Historia = () => {
    const cookies = new Cookies();

    const navigate = useNavigate();
    const baseUrl = environment.baseUrl + 'historias';

    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0
    });

    const [EntidadNewDialog, setEntidadNewDialog] = useState(false);
    const [deleteProductDialog, setDeleteProductDialog] = useState(false);
    const [product, setProduct] = useState(emptyHistoria);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const toast = useRef(null);
    const dt = useRef(null);

    const [archivoImagen, setArchivoImagen] = useState(null);
    const [imagenPreviewUrl, setImagenPreviewUrl] = useState(null);
    const [imageUpdateDialog, setImageUpdateDialog] = useState(false);

    const actionMenuRef = useRef(null);
    const [actionMenuModel, setActionMenuModel] = useState([]);
    const [currentActionRow, setCurrentActionRow] = useState(null);


    const peticionGet = useCallback(async () => {
        setLoading(true);
        try {
            const page = lazyParams.first / lazyParams.rows;
            const response = await axios.get(baseUrl, {
                params: {
                    page,
                    size: lazyParams.rows,
                    // Si el backend tuviera filtro, se agregaría aquí: search: globalFilter || ''
                }
            });
            setData(response.data.content);
            setTotalRecords(response.data.totalElements);
        } catch (error) {
            console.error("Error en peticionGet:", error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las historias', life: 5000 });
        } finally {
            setLoading(false);
        }
    }, [baseUrl, lazyParams]);


    const peticionPost = async () => {
        if (!archivoImagen) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Debe adjuntar una imagen.', life: 3000 });
            return;
        }

        const historiaData = {
            titulo: product.titulo,
            descripcion: product.descripcion,
            redactor: product.redactor,
            fechaAcontecimiento: product.fechaAcontecimiento ? format(new Date(product.fechaAcontecimiento), 'yyyy-MM-dd') : null,
        };

        const formData = new FormData();
        formData.append('datos', JSON.stringify(historiaData));
        formData.append('imagen', archivoImagen);

        try {
            const response = await axios.post(baseUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Historia registrada correctamente', life: 3000, });
            hideDialogNew();
            peticionGet();
        } catch (error) {
            console.error("Error en peticionPost:", error.response || error);
            toast.current.show({ severity: 'error', summary: 'Error de Registro', detail: 'Verifique los datos o inténtelo más tarde', life: 5000, });
        }
    };


    const peticionPut = async () => {
        const historiaToUpdate = {
            titulo: product.titulo,
            descripcion: product.descripcion,
            redactor: product.redactor,
            fechaAcontecimiento: product.fechaAcontecimiento ? format(new Date(product.fechaAcontecimiento), 'yyyy-MM-dd') : null,
        };

        try {
            const response = await axios.put(`${baseUrl}/${product.id}`, historiaToUpdate);

            const dataNueva = data.map((u) => {
                if (u.id === product.id) {
                    return { ...u, ...historiaToUpdate };
                }
                return u;
            });
            setData(dataNueva);

            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Historia modificada correctamente', life: 3000, });
            hideDialogNew();
        } catch (error) {
            console.error("Error en peticionPut:", error.response || error);
            toast.current.show({ severity: 'error', summary: 'Error de Modificación', detail: 'Verifique los datos o inténtelo más tarde', life: 5000, });
        }
    };

    const peticionPutImagen = async () => {
        if (!archivoImagen) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Debe seleccionar una nueva imagen.', life: 3000 });
            return;
        }

        const formData = new FormData();
        // Clave 'imagen' debe coincidir con @RequestPart("imagen") en el Controller de imagen
        formData.append('imagen', archivoImagen);

        try {
            // Endpoint que creamos: PUT /api/historias/{id}/imagen
            await axios.put(`${baseUrl}/${product.id}/imagen`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Imagen actualizada correctamente', life: 3000, });
            hideDialogImageUpdate();
            peticionGet();
        } catch (error) {
            console.error("Error en peticionPutImagen:", error.response || error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al actualizar la imagen.', life: 5000, });
        }
    };

    const peticionDelete = async () => {
        try {
            await axios.delete(`${baseUrl}/${product.id}`);
            const updatedProducts = data.filter(val => val.id !== product.id);
            setData(updatedProducts);
            setDeleteProductDialog(false);
            setProduct(emptyHistoria);
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Historia eliminada correctamente', life: 3000 });
        } catch (error) {
            console.error("Error en peticionDelete:", error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al eliminar la historia', life: 5000 });
        }
    };

    const onPage = (event) => {
        setLazyParams((prevState) => ({
            ...prevState,
            first: event.first,
            rows: event.rows
        }));
    };

    useEffect(() => {
        const role = cookies.get('role');
        if (role !== 'ADMIN') {
            navigate('/');
        }
        peticionGet();
    }, [lazyParams, globalFilter, peticionGet, navigate]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return format(date, 'dd/MM/yyyy');
    };

    const openNew = () => {
        setProduct(emptyHistoria);
        setArchivoImagen(null);
        setImagenPreviewUrl(null);
        setSubmitted(false);
        setEntidadNewDialog(true);
    };

    const hideDialogNew = () => {
        setSubmitted(false);
        setEntidadNewDialog(false);
        setProduct(emptyHistoria);
        setArchivoImagen(null);
        setImagenPreviewUrl(null);
    };

    const hideDialogImageUpdate = () => {
        setImageUpdateDialog(false);
        setArchivoImagen(null);
        setImagenPreviewUrl(null);
    }

    const editProduct = (product) => {
        const fechaAcontecimientoDate = product.fechaAcontecimiento ? new Date(product.fechaAcontecimiento) : null;

        setProduct({
            ...product,
            fechaAcontecimiento: fechaAcontecimientoDate
        });
        setArchivoImagen(null);
        setImagenPreviewUrl(null);
        setEntidadNewDialog(true);
    };

    const openUpdateImageDialog = (rowData) => {
        setProduct(rowData);
        setArchivoImagen(null);
        setImagenPreviewUrl(null);
        setImageUpdateDialog(true);
    };

    const saveProduct = () => {
        setSubmitted(true);

        if (!product.titulo || !product.descripcion || !product.fechaAcontecimiento || !product.redactor) {
            toast.current.show({
                severity: 'error',
                summary: 'Validación',
                detail: 'Por favor, complete Título, Descripción y Fecha de Acontecimiento.',
                life: 5000,
            });
            return;
        }
        if (!product.id && !archivoImagen) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Debe adjuntar una imagen para el registro.', life: 3000 });
            return;
        }
        if (product.id) {
            peticionPut();
        } else {
            console.log("Guardando nueva historia...");
            peticionPost();
        }
    };

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _product = { ...product };
        _product[name] = val;
        setProduct(_product);
    };

    const onCalendarChange = (e, name) => {
        let _product = { ...product };
        _product[name] = e.value;
        setProduct(_product);
    }

    const onFileSelect = (e) => {
        if (e.files && e.files.length > 0) {
            const file = e.files[0];
            setArchivoImagen(file);
            setImagenPreviewUrl(URL.createObjectURL(file));
        }
    };

    const onFileRemove = () => {
        setArchivoImagen(null);
        setImagenPreviewUrl(null);
    };


    const confirmDeleteProduct = (product) => {
        setProduct(product);
        setDeleteProductDialog(true);
    }

    const hideDeleteProductDialog = () => {
        setDeleteProductDialog(false);
    }

    const deleteProduct = () => {
        peticionDelete();
    };

    const deleteProductDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={hideDeleteProductDialog} />
            <Button label="Sí, Eliminar" icon="pi pi-check" className="p-button-text p-button-danger" onClick={deleteProduct} />
        </>
    );

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    <Button
                        label="Registrar Historia"
                        icon="pi pi-plus"
                        className="p-button-outlined p-button-success mr-2"
                        onClick={openNew}
                    />
                </div>
            </React.Fragment>
        );
    };

    const idBodyTemplate = (rowData) => {
        return <span className="text-sm font-semibold">{rowData.id}</span>;
    };

    const tituloBodyTemplate = (rowData) => {
        return <span className="font-medium">{rowData.titulo}</span>;
    };

    const fechaPublicacionBodyTemplate = (rowData) => {
        const dateValue = rowData.fechaPublicacion;
        return <span className="text-sm">{formatDate(dateValue)}</span>;
    };

    const fechaAcontecimientoBodyTemplate = (rowData) => {
        const dateValue = rowData.fechaAcontecimiento;
        return <span className="text-sm">{formatDate(dateValue)}</span>;
    };

    const datosBodyTemplate = (rowData) => {
        const imageUrl = `${environment.baseUrl}imagenes/${rowData.urlImagen}`;

        return (
            <div className="flex justify-content-center">
                <img
                    src={imageUrl}
                    alt={rowData.titulo}
                    className="shadow-2"
                    width="70"
                    height="50"
                    style={{ objectFit: 'cover' }}
                    onError={(e) => {
                        e.target.src = "/logos/imagen-no-disponible.png"; // Usar imagen por defecto
                        e.target.style.transform = "scale(0.8)";
                        e.target.style.objectFit = "contain";
                    }}
                />
            </div>
        );
    };

    const showActionMenu = (event, rowData) => {
        setCurrentActionRow(rowData);

        const items = [
            {
                label: 'Modificar Texto',
                icon: 'pi pi-pencil',
                command: () => {
                    editProduct(rowData);
                },
            },
            {
                label: 'Actualizar Imagen',
                icon: 'pi pi-image',
                command: () => {
                    openUpdateImageDialog(rowData);
                },
            },
            {
                separator: true
            },
            {
                label: 'Eliminar Historia',
                icon: 'pi pi-trash',
                className: 'p-menuitem-link p-text-danger',
                command: () => {
                    confirmDeleteProduct(rowData);
                },
            },
        ];

        setActionMenuModel(items);
        actionMenuRef.current.toggle(event);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button
                    icon="pi pi-ellipsis-v"
                    title="Opciones"
                    className="p-button-rounded p-button-text p-button-secondary"
                    onClick={(e) => showActionMenu(e, rowData)}
                    aria-controls="popup_menu"
                    aria-haspopup
                />
            </div>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 style={{ margin: 0 }}>Gestión de Historias/Publicaciones</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                {/*<i className="pi pi-search" style={{ color: PJ_BLUE_DARK }} />*/}
                <InputText type="search" value={globalFilter || ''} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Buscar..." onKeyUp={peticionGet} />
            </span>
        </div>
    );

    const productDialogFooterNew = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={hideDialogNew} />
            <Button label="Guardar" icon="pi pi-check" style={{ color: PJ_BLUE_DARK, borderColor: PJ_BLUE_DARK }} onClick={saveProduct} />
        </>
    );

    const imageUpdateDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={hideDialogImageUpdate} />
            <Button label="Actualizar Imagen" icon="pi pi-upload" style={{ color: PJ_BLUE_DARK, borderColor: PJ_BLUE_DARK }} onClick={peticionPutImagen} />
        </>
    );

    return (
        <div className="grid">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <Menu
                        model={actionMenuModel}
                        popup
                        ref={actionMenuRef}
                        id="popup_menu"
                        style={{ width: '15rem' }}
                    />
                    <Toolbar className="mb-4" left={leftToolbarTemplate} right={null}></Toolbar> {/* Eliminado ExportCSV */}
                    <DataTable ref={dt} value={data} dataKey="id"
                        paginator className="datatable-responsive"
                        lazy scrollable
                        first={lazyParams.first}
                        rows={lazyParams.rows}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        loading={loading}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} Historias"
                        globalFilter={globalFilter} emptyMessage="No existen registros." header={header}>

                        <Column field="id" header="ID" style={{ minWidth: '5rem' }} body={idBodyTemplate}></Column>
                        <Column header="Imagen" style={{ minWidth: '8rem' }} body={datosBodyTemplate}></Column>
                        <Column field="titulo" header="Título" style={{ minWidth: '20rem' }} body={tituloBodyTemplate} ></Column>
                        <Column field="fechaAcontecimiento" header="Fecha Acontecimiento" style={{ minWidth: '12rem' }} body={fechaAcontecimientoBodyTemplate} ></Column>
                        <Column field="fechaPublicacion" header="Publicación" style={{ minWidth: '10rem' }} body={fechaPublicacionBodyTemplate} ></Column>
                        <Column header="Acciones" body={actionBodyTemplate} style={{ minWidth: '10rem', textAlign: 'center' }}></Column>
                    </DataTable>

                    <Dialog visible={EntidadNewDialog} style={{ width: '800px' }} header={product.id ? "Modificar Historia" : "Registrar Historia"} modal className="p-fluid" footer={productDialogFooterNew} onHide={hideDialogNew}>

                        <div className="formgrid grid">
                            <div className="field col-12 md:col-6">
                                <label htmlFor="titulo">Título de la Historia</label>
                                <InputText id="titulo" name="titulo" value={product.titulo} onChange={(e) => onInputChange(e, 'titulo')} required autoFocus className={classNames({ 'p-invalid': submitted && !product.titulo })} />
                                {submitted && !product.titulo && <small className="p-invalid">El título es requerido.</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="redactor">Redactor (Autor)</label>
                                <InputText
                                    id="redactor"
                                    name="redactor"
                                    value={product.redactor}
                                    onChange={(e) => onInputChange(e, 'redactor')}
                                    required
                                    maxLength={255} // Coincide con el length=40 de tu @Column
                                    className={classNames({ 'p-invalid': submitted && !product.redactor })}
                                />
                                {submitted && !product.redactor && <small className="p-invalid">El nombre del redactor es requerido.</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="fechaAcontecimiento">Fecha de Acontecimiento</label>
                                <Calendar
                                    id="fechaAcontecimiento"
                                    name="fechaAcontecimiento"
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    locale='es'
                                    showButtonBar
                                    value={product.fechaAcontecimiento} // Ya es un Date si viene de editProduct
                                    onChange={(e) => onCalendarChange(e, 'fechaAcontecimiento')}
                                    required
                                    className={classNames({ 'p-invalid': submitted && !product.fechaAcontecimiento })}
                                />
                                {submitted && !product.fechaAcontecimiento && <small className="p-invalid">La fecha es requerida.</small>}
                            </div>
                        </div>

                        <div className="formgrid grid">
                            <div className="field col-12">
                                <label htmlFor="descripcion">Descripción</label>
                                <InputTextarea id="descripcion" value={product.descripcion} onChange={(e) => onInputChange(e, 'descripcion')} required rows={10} cols={20} className={classNames({ 'p-invalid': submitted && !product.descripcion })} />
                                {submitted && !product.descripcion && <small className="p-invalid">La descripción es requerida.</small>}
                            </div>
                        </div>

                        {/* MUESTRA LA SUBIDA DE IMAGEN SOLO EN MODO REGISTRO */}
                        {!product.id && (
                            <div className="field col-12">
                                <label htmlFor="imagen">Imagen Principal *</label>
                                {/* Previsualización de la nueva imagen */}
                                {imagenPreviewUrl && (
                                    <div className="mb-2">
                                        <p className="text-sm">Previsualización:</p>
                                        <img src={imagenPreviewUrl} alt="Previsualización" width="150" className="shadow-2 mb-2" />
                                    </div>
                                )}

                                <FileUpload
                                    name="imagen"
                                    mode="basic"
                                    chooseLabel="Seleccionar Imagen"
                                    accept="image/*"
                                    maxFileSize={50000000} // 50MB
                                    onSelect={onFileSelect}
                                    onClear={onFileRemove}
                                    auto={false}
                                    className={classNames({ 'p-invalid': submitted && !archivoImagen })}
                                />
                                {submitted && !archivoImagen && <small className="p-invalid">La imagen es requerida para el registro.</small>}
                            </div>
                        )}

                    </Dialog>

                    {/* *** DIALOGO DE ACTUALIZACIÓN DE IMAGEN *** */}
                    <Dialog visible={imageUpdateDialog} style={{ width: '450px' }} header={`Actualizar Imagen: ${product.titulo}`} modal className="p-fluid" footer={imageUpdateDialogFooter} onHide={hideDialogImageUpdate}>
                        <div className="field">
                            <p className="text-sm font-bold mb-2">Imagen actual:</p>
                            <img
                                src={`${environment.baseUrl}imagenes/${product.urlImagen}`}
                                alt="Imagen Actual"
                                width="100%"
                                style={{ objectFit: 'cover', height: '150px' }}
                                className="shadow-2 mb-4"
                            />
                        </div>

                        <div className="field">
                            <label htmlFor="nuevaImagen">Nueva Imagen *</label>
                            {imagenPreviewUrl && (
                                <div className="mb-2">
                                    <p className="text-sm">Previsualización:</p>
                                    <img src={imagenPreviewUrl} alt="Previsualización" width="100%" style={{ objectFit: 'cover', height: '150px' }} className="shadow-2 mb-2" />
                                </div>
                            )}
                            <FileUpload
                                name="imagen"
                                mode="basic"
                                chooseLabel="Seleccionar Nueva Imagen"
                                accept="image/*"
                                maxFileSize={1000000}
                                onSelect={onFileSelect}
                                onClear={onFileRemove}
                                auto={false}
                            />
                        </div>
                    </Dialog>


                    {/* DIALOGO DE ELIMINACIÓN */}
                    <Dialog visible={deleteProductDialog} style={{ width: '450px' }} header="Confirmación de Eliminación" modal footer={deleteProductDialogFooter} onHide={hideDeleteProductDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {product && <span>¿Está seguro de eliminar la historia: <b>{product.titulo}</b>? Esta acción es irreversible.</span>}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default Historia;